import { FirebaseError } from 'firebase/app';
import Sentry from '../config/sentry';
import { logger } from './logger';

const log = logger.createScope('ErrorHandlingService');

export interface ErrorDetails {
  message: string;
  code?: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  category: 'network' | 'auth' | 'database' | 'validation' | 'system' | 'user';
  userMessage: string;
  retryable: boolean;
  requiresAuth?: boolean;
}

export class ErrorHandlingService {
  private static instance: ErrorHandlingService;
  private errorHandlers: Map<string, (error: unknown) => ErrorDetails> = new Map();

  private constructor() {
    this.initializeDefaultHandlers();
  }

  public static getInstance(): ErrorHandlingService {
    if (!ErrorHandlingService.instance) {
      ErrorHandlingService.instance = new ErrorHandlingService();
    }
    return ErrorHandlingService.instance;
  }

  private initializeDefaultHandlers() {
    // Firebase Authentication Errors
    this.registerHandler('auth/user-not-found', () => ({
      message: 'User account not found',
      code: 'auth/user-not-found',
      severity: 'medium',
      category: 'auth',
      userMessage: 'No account found with this email address. Please check your email or sign up.',
      retryable: false,
    }));

    this.registerHandler('auth/wrong-password', () => ({
      message: 'Incorrect password',
      code: 'auth/wrong-password',
      severity: 'medium',
      category: 'auth',
      userMessage: 'Incorrect password. Please try again.',
      retryable: true,
    }));

    this.registerHandler('auth/too-many-requests', () => ({
      message: 'Too many failed attempts',
      code: 'auth/too-many-requests',
      severity: 'high',
      category: 'auth',
      userMessage: 'Too many failed login attempts. Please try again later.',
      retryable: true,
    }));

    this.registerHandler('auth/network-request-failed', () => ({
      message: 'Network error during authentication',
      code: 'auth/network-request-failed',
      severity: 'high',
      category: 'network',
      userMessage: 'Network connection failed. Please check your internet connection and try again.',
      retryable: true,
    }));

    // Firebase Database Errors
    this.registerHandler('permission-denied', () => ({
      message: 'Permission denied',
      code: 'permission-denied',
      severity: 'high',
      category: 'database',
      userMessage: 'You don\'t have permission to perform this action. Please contact support.',
      retryable: false,
      requiresAuth: true,
    }));

    this.registerHandler('unavailable', () => ({
      message: 'Service temporarily unavailable',
      code: 'unavailable',
      severity: 'high',
      category: 'database',
      userMessage: 'Service is temporarily unavailable. Please try again in a few moments.',
      retryable: true,
    }));

    // Network Errors
    this.registerHandler('NetworkError', () => ({
      message: 'Network connection failed',
      code: 'network-error',
      severity: 'high',
      category: 'network',
      userMessage: 'Unable to connect to the server. Please check your internet connection.',
      retryable: true,
    }));

    this.registerHandler('fetch-failed', () => ({
      message: 'Request failed',
      code: 'fetch-failed',
      severity: 'medium',
      category: 'network',
      userMessage: 'Request failed. Please try again.',
      retryable: true,
    }));

    // Validation Errors
    this.registerHandler('ValidationError', () => ({
      message: 'Validation failed',
      code: 'validation-error',
      severity: 'low',
      category: 'validation',
      userMessage: 'Please check your input and try again.',
      retryable: false,
    }));

    // System Errors
    this.registerHandler('ChunkLoadError', () => ({
      message: 'Failed to load application chunk',
      code: 'chunk-load-error',
      severity: 'critical',
      category: 'system',
      userMessage: 'Failed to load part of the application. Please refresh the page.',
      retryable: true,
    }));

    this.registerHandler('TypeError', () => ({
      message: 'JavaScript type error',
      code: 'type-error',
      severity: 'critical',
      category: 'system',
      userMessage: 'An unexpected error occurred. Please refresh the page.',
      retryable: true,
    }));
  }

  public registerHandler(errorCode: string, handler: (error: unknown) => ErrorDetails) {
    this.errorHandlers.set(errorCode, handler);
  }

  public handleError(error: unknown): ErrorDetails {
    const errName = error instanceof Error ? error.name : undefined;
    const errMessage = error instanceof Error ? error.message : undefined;
    log.debug('Error analysis started', { errorName: errName, errorMessage: errMessage });

    let errorDetails: ErrorDetails;

    // Check custom handlers first (for objects with a code property)
    const errCode = (error != null && typeof error === 'object' && 'code' in error && typeof (error as { code: unknown }).code === 'string')
      ? (error as { code: string }).code
      : undefined;
    if (errCode && this.errorHandlers.has(errCode)) {
      const handler = this.errorHandlers.get(errCode)!;
      errorDetails = handler(error);
    }
    // Handle Firebase errors
    else if (this.isFirebaseError(error)) {
      errorDetails = this.handleFirebaseError(error);
    }
    // Handle network errors
    else if (this.isNetworkError(error)) {
      errorDetails = this.handleNetworkError(error);
    }
    // Handle validation errors
    else if (this.isValidationError(error)) {
      errorDetails = this.handleValidationError(error);
    }
    // Handle JavaScript errors
    else if (error instanceof Error) {
      errorDetails = this.handleJavaScriptError(error);
    }
    // Handle unknown errors
    else {
      errorDetails = this.handleUnknownError(error);
    }

    log.debug('Processed error details', { 
      code: errorDetails.code, 
      severity: errorDetails.severity, 
      category: errorDetails.category,
      retryable: errorDetails.retryable
    });

    // Log to external service if needed
    this.logToExternalService(error, errorDetails);

    return errorDetails;
  }

  private isFirebaseError(error: unknown): error is FirebaseError {
    if (error == null || typeof error !== 'object') return false;
    const err = error as Record<string, unknown>;
    const code = typeof err.code === 'string' ? err.code : '';
    return code !== '' && (
      code.includes('/') ||
      err.name === 'FirebaseError' ||
      // Common Firebase error codes without a '/' prefix
      ['permission-denied', 'unavailable', 'not-found', 'already-exists',
       'resource-exhausted', 'failed-precondition', 'aborted', 'out-of-range',
       'unimplemented', 'internal', 'data-loss', 'unauthenticated', 'cancelled'
      ].includes(code)
    );
  }

  private isNetworkError(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false;
    const err = error as Record<string, unknown>;
    const message = (typeof err.message === 'string' ? err.message : '').toLowerCase();
    return (
      (error instanceof TypeError && message.includes('fetch')) ||
      err.name === 'NetworkError' ||
      message.includes('network') ||
      message.includes('offline')
    );
  }

  private isValidationError(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false;
    const err = error as Record<string, unknown>;
    return err.name === 'ValidationError' || err.type === 'validation';
  }

  private handleFirebaseError(error: FirebaseError): ErrorDetails {
    const handler = this.errorHandlers.get(error.code);
    if (handler) {
      return handler(error);
    }

    // Default Firebase error handling
    return {
      message: error.message,
      code: error.code,
      severity: 'medium',
      category: 'database',
      userMessage: 'A service error occurred. Please try again.',
      retryable: true,
    };
  }

  private handleNetworkError(error: unknown): ErrorDetails {
    const handler = this.errorHandlers.get('NetworkError');
    if (handler) {
      return handler(error);
    }

    const message = (error instanceof Error) ? error.message : 'Network error';
    return {
      message,
      code: 'network-error',
      severity: 'high',
      category: 'network',
      userMessage: 'Network connection failed. Please check your internet connection.',
      retryable: true,
    };
  }

  private handleValidationError(error: unknown): ErrorDetails {
    const err = error as Record<string, unknown>;
    const message = typeof err.message === 'string' ? err.message : 'Validation failed';
    const userMessage = typeof err.userMessage === 'string' ? err.userMessage : 'Please check your input and try again.';
    return {
      message,
      code: 'validation-error',
      severity: 'low',
      category: 'validation',
      userMessage,
      retryable: false,
    };
  }

  private handleJavaScriptError(error: Error): ErrorDetails {
    const handler = this.errorHandlers.get(error.name);
    if (handler) {
      return handler(error);
    }

    return {
      message: error.message,
      code: error.name,
      severity: 'critical',
      category: 'system',
      userMessage: 'An unexpected error occurred. Please refresh the page.',
      retryable: true,
    };
  }

  private handleUnknownError(error: unknown): ErrorDetails {
    return {
      message: typeof error === 'string' ? error : 'Unknown error',
      code: 'unknown-error',
      severity: 'medium',
      category: 'system',
      userMessage: 'An unexpected error occurred. Please try again.',
      retryable: true,
    };
  }

  private logToExternalService(originalError: unknown, errorDetails: ErrorDetails) {
    if (process.env.NODE_ENV !== 'production') return;

    const err = originalError instanceof Error
      ? originalError
      : new Error(errorDetails.message);

    Sentry.captureException(err, {
      tags: {
        severity: errorDetails.severity,
        category: errorDetails.category,
      },
      extra: {
        code: errorDetails.code,
        retryable: errorDetails.retryable,
        userMessage: errorDetails.userMessage,
      },
    });
  }

  // Utility methods for components
  public shouldShowToUser(errorDetails: ErrorDetails): boolean {
    return errorDetails.severity !== 'low' || errorDetails.category === 'validation';
  }

  public shouldRetry(errorDetails: ErrorDetails): boolean {
    return errorDetails.retryable;
  }

  public getRetryDelay(errorDetails: ErrorDetails, attempt: number): number {
    // Exponential backoff with jitter
    const baseDelay = 1000; // 1 second
    const maxDelay = 30000; // 30 seconds
    const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
    const jitter = delay * 0.1 * Math.random();
    return delay + jitter;
  }
}

// Export singleton instance
export const errorHandler = ErrorHandlingService.getInstance();

// React hook for easy error handling in components
export const useErrorHandler = () => {
  return {
    handleError: (error: unknown) => errorHandler.handleError(error),
    shouldShowToUser: (errorDetails: ErrorDetails) => errorHandler.shouldShowToUser(errorDetails),
    shouldRetry: (errorDetails: ErrorDetails) => errorHandler.shouldRetry(errorDetails),
    getRetryDelay: (errorDetails: ErrorDetails, attempt: number) => 
      errorHandler.getRetryDelay(errorDetails, attempt),
  };
};